import {
	type DefaultMessagesResponse,
	type PlacementMessageFrontendFactory,
	type AnyFrontendStage,
	type NoProps,
} from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { Box } from '@atlaskit/primitives/box';

import { Inline } from '@atlaskit/primitives/inline';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { useState, useCallback, useRef, useEffect } from 'react';

import type { MessageTemplateContext } from './test-messages/your-test-message';

import { BannerUI } from './ui/BannerUI';

import { createFrontend } from './factories';

export type PlacementFrontendProps = NoProps;

/** Props injected by the placement into each message template's render Component */
export type CustomPlacementProps = {
	onSnoozeClick?: () => void;
};

export { createFrontend };

const SNOOZE_FADE_DURATION = 300;

// ms

const cardsInlineStyles = xcss({
	marginTop: 'space.100',
});

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>((Messages) => () => {
	const { data, error, isLoading } =
		usePlacementApi<DefaultMessagesResponse<MessageTemplateContext>>('/');
	const [snoozingIds, setSnoozingIds] = useState<Set<string>>(new Set());
	const [snoozedIds, setSnoozedIds] = useState<Set<string>>(new Set());
	const timerIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

	// Clear all pending timers on unmount to prevent state updates on unmounted component
	useEffect(() => {
		return () => {
			timerIdsRef.current.forEach(clearTimeout);
		};
	}, []);

	const handleSnooze = useCallback((messageInstanceId: string) => {
		// Start fade out
		setSnoozingIds((prev) => new Set(prev).add(messageInstanceId));
		// Remove after fade completes — store timer ID for cleanup
		const timerId = setTimeout(() => {
			setSnoozedIds((prev) => new Set(prev).add(messageInstanceId));
			setSnoozingIds((prev) => {
				const next = new Set(prev);
				next.delete(messageInstanceId);
				return next;
			});
		}, SNOOZE_FADE_DURATION);
		timerIdsRef.current.push(timerId);
	}, []);

	if (error) {
		throw error;
	}

	const visibleMessages = (data?.messages ?? []).filter(
		(m) => !snoozedIds.has(m.messageInstanceId),
	);
	const messageCount = isLoading ? 0 : visibleMessages.length;

	if (!isLoading && messageCount === 0 && snoozingIds.size === 0) {
		return null;
	}

	return (
		<BannerUI
			isLoading={isLoading}
			messageCount={messageCount}
			renderMessages={() => (
				<Inline space="space.150" alignBlock="stretch" xcss={cardsInlineStyles}>
					{visibleMessages.map((props) => (
						<Box
							key={props.messageInstanceId}
							style={{
								flex: '0 0 calc(33.333% - 8px)',
								minWidth: 0,
								opacity: snoozingIds.has(props.messageInstanceId) ? 0 : 1,
								transition: `opacity ${SNOOZE_FADE_DURATION}ms ease`,
							}}
						>
							<Messages.render
								{...props}
								onSnoozeClick={() => handleSnooze(props.messageInstanceId)}
							/>
						</Box>
					))}
				</Inline>
			)}
		/>
	);
});
